import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { UserDetailDrawer } from "./UserDetailDrawer";
import EditUserModal from "./EditUserModal";
import {
  Search,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { usersApi } from "../utils/api";

export function Users() {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleting, setDeleting] = useState(false);

  const [filters, setFilters] = useState({
    first_utm_source: "",
    first_utm_medium: "",
    first_utm_campaign: "",
    latest_utm_source: "",
    latest_utm_medium: "",
    latest_utm_campaign: "",
    country: "",
    state: "",
    city: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        setUsers([]);
        setTotalPages(0);
        setTotal(0);
        setLoading(false);
        return;
      }

      const response = await usersApi.getAllUsers(
        currentPage,
        limit,
        searchTerm,
        filters
      );

      if (response.data && response.data.users && response.data.pagination) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.totalPages);
        setTotal(response.data.pagination.total);
      } else {
        console.error("Invalid API response structure for users");
        setUsers([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, limit, filters]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEditUser = (user) => {
    let data = { ...user };
    delete data?.assigned_qr_codes;
    setSelectedUserId(user?._id ?? user?.id);
    setEditUserData(data);
    setIsEditModalOpen(true);
  };

  const afterEditComplete = () => {
    fetchUsers();
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  const handleViewDetails = (user) => {
    setSelectedUserId(user?._id ?? user?.id);
    setUserData(user);
    setIsDrawerOpen(true);
  };

  const confirmDelete = async (user) => {
    const ok = window.confirm(`Are you sure you want to delete ${user.name}?`);
    if (ok) {
      setDeleting(true);
      const payload = { id: user?.id };
      const response = await usersApi.deleteUser(payload);
      if (response?.statusCode === 200) {
        setDeleting(false);
        fetchUsers();
      }
    }
  };

  // Parse registrationDate: "DD/MM/YYYY, hh:mm am/pm"
  const parseRegistrationDate = (value) => {
    if (!value) return null;
    try {
      const [datePart, timePartRaw] = value.split(",").map((s) => s.trim());
      const [dd, mm, yyyy] = datePart.split("/").map(Number);
      const [timePart, ampm] = timePartRaw.split(" ").map((s) => s.trim());
      const [hhStr, minStr] = timePart.split(":");
      let hours = Number(hhStr);
      const minutes = Number(minStr);
      if (ampm.toLowerCase() === "pm" && hours < 12) hours += 12;
      if (ampm.toLowerCase() === "am" && hours === 12) hours = 0;
      const d = new Date(yyyy, mm - 1, dd, hours, minutes, 0, 0);
      if (isNaN(d.getTime())) return null;
      return d;
    } catch {
      return null;
    }
  };

  const sortedUsers = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => {
      const da = parseRegistrationDate(a.registrationDate)?.getTime() ?? 0;
      const db = parseRegistrationDate(b.registrationDate)?.getTime() ?? 0;
      return db - da; // descending
    });
    return copy;
  }, [users]);

  const filteredUsers = useMemo(() => sortedUsers, [sortedUsers]);

  return (
    <div className="space-y-6">
      {/* Loader Overlay */}
      {deleting && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
        </div>
      )}
      <div>
        <h1 className="mb-2">Users</h1>
        <p className="text-muted-foreground">
          Manage registered users and their QR code assignments
        </p>
      </div>

      {/* Search */}
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone number..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Limit selector */}
            <Select value={limit.toString()} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Items per page" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full sm:w-auto"
            >
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="block text-sm mb-1 text-foreground">First Source</label>
                <Input
                  placeholder="e.g. google"
                  value={filters.first_utm_source}
                  onChange={(e) => { setFilters({...filters, first_utm_source: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">First Medium</label>
                <Input
                  placeholder="e.g. cpc"
                  value={filters.first_utm_medium}
                  onChange={(e) => { setFilters({...filters, first_utm_medium: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">First Campaign</label>
                <Input
                  placeholder="e.g. summer"
                  value={filters.first_utm_campaign}
                  onChange={(e) => { setFilters({...filters, first_utm_campaign: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">Latest Source</label>
                <Input
                  placeholder="e.g. direct"
                  value={filters.latest_utm_source}
                  onChange={(e) => { setFilters({...filters, latest_utm_source: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">Latest Medium</label>
                <Input
                  placeholder="e.g. email"
                  value={filters.latest_utm_medium}
                  onChange={(e) => { setFilters({...filters, latest_utm_medium: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">Latest Campaign</label>
                <Input
                  placeholder="e.g. newsletter"
                  value={filters.latest_utm_campaign}
                  onChange={(e) => { setFilters({...filters, latest_utm_campaign: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">Country</label>
                <Input
                  placeholder="e.g. India"
                  value={filters.country}
                  onChange={(e) => { setFilters({...filters, country: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">State/Region</label>
                <Input
                  placeholder="e.g. Gujarat"
                  value={filters.state}
                  onChange={(e) => { setFilters({...filters, state: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div>
                <label className="block text-sm mb-1 text-foreground">City</label>
                <Input
                  placeholder="e.g. Ahmedabad"
                  value={filters.city}
                  onChange={(e) => { setFilters({...filters, city: e.target.value}); setCurrentPage(1); }}
                />
              </div>
              <div className="col-span-full">
                <Button variant="outline" onClick={() => {
                  setFilters({
                    first_utm_source: "", first_utm_medium: "", first_utm_campaign: "",
                    latest_utm_source: "", latest_utm_medium: "", latest_utm_campaign: "",
                    country: "", state: "", city: ""
                  });
                  setCurrentPage(1);
                }}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>User List ({total})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading users...</div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No users found</div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Assigned QR Codes</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Registration Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone}</TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {user.assigned_qr_codes.length} code
                          {user.assigned_qr_codes.length !== 1 ? "s" : ""}
                        </span>
                      </TableCell>
                      <TableCell>{user.status ? "Yes" : "No"}</TableCell>
                      <TableCell>{user.registrationDate}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(user)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            onClick={() => confirmDelete(user)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <UserDetailDrawer
        user={userData}
        userId={selectedUserId}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />

      <EditUserModal
        defaultValues={editUserData}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onEdit={afterEditComplete}
      />
    </div>
  );
}
